/**
 =========================================================
 * Material Dashboard 2 PRO React TS - v1.0.2
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useEffect, useMemo, useState } from 'react';

// react-router components
import { useLocation } from 'react-router-dom';

// @mui material components
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import Icon from '@mui/material/Icon';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';

// Material Dashboard 2 PRO React TS exampless
import Sidenav from 'examples/Sidenav';
import Configurator from 'examples/Configurator';

// Material Dashboard 2 PRO React TS themes
import theme from 'assets/theme';
import themeRTL from 'assets/theme/theme-rtl';

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from 'assets/theme-dark';
import themeDarkRTL from 'assets/theme-dark/theme-rtl';

// Global scrollbar styling (dark/light)
import { scrollbarStyles } from 'assets/theme/scrollbar';

// RTL plugins
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

// Material Dashboard 2 PRO React TS routes
import routes from 'routes';

// Material Dashboard 2 PRO React TS contexts
import { setOpenConfigurator, useMaterialUIController } from 'context';

// Images
import logoSidenavBranca from 'assets/images/brand/logoMGCodeSideNav.png';
import logoSidenavPreta from 'assets/images/brand/logoMGCodeSideNavDarkMode.png';
import RoutesApp from 'guard.routes';
import LoadingScreen from 'components/utils/LoadingScreen';
import { useAuth } from 'context/auth-context';
import { filterRoutes, allowedScreens } from 'functions/allowed-routes';
import { useBrowserNotifications } from 'hooks/use-browser-notifications.hook';
import { LoginScreen } from 'layouts/authentication/sign-in';
import { ForgotPasswordScreen } from 'layouts/authentication/password/forgot-password-screen';
import { ResetPasswordScreen } from 'layouts/authentication/password/reset-password-screen';
import { AppRoute } from 'types/app-route';
import { environment } from 'routes/environment.router';

export default function App() {
    const [controller, dispatch] = useMaterialUIController();
    const {
        direction,
        layout,
        openConfigurator,
        sidenavColor,
        transparentSidenav,
        whiteSidenav,
        darkMode,
        isLoading,
    } = controller;
    const [rtlCache, setRtlCache] = useState(null);
    const { pathname } = useLocation();
    const { authenticated } = useAuth();

    useBrowserNotifications(authenticated);

    // Recalcula ao entrar e ao sair: o menu sai do token, e trocar de usuário
    // sem isso deixaria o menu do anterior na tela.
    const routesApp = useMemo<AppRoute[]>(
        () => filterRoutes(routes, allowedScreens()),
        [authenticated]);

    // Cache for the rtl
    useMemo(() => {
        const cacheRtl = createCache({
            key: 'rtl',
            stylisPlugins: [rtlPlugin],
        });

        setRtlCache(cacheRtl);
    }, []);

    // Change the openConfigurator state
    const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

    // Setting the dir attribute for the body element
    useEffect(() => {
        document.body.setAttribute('dir', direction);
    }, [direction]);

    // Setting page scroll to 0 when changing the route
    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    const configsButton = (
        <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3.25rem"
            height="3.25rem"
            bgColor="white"
            shadow="sm"
            borderRadius="50%"
            position="fixed"
            right="2rem"
            bottom="2rem"
            zIndex={99}
            color="dark"
            sx={{ cursor: 'pointer' }}
            onClick={handleConfiguratorOpen}
        >
            <Icon fontSize="small" color="inherit">
                settings
            </Icon>
        </MDBox>
    );

    // O link de redefinição chega por e-mail e abre com ou sem sessão: mandar
    // para o quadro quem já estava logado pareceria que o link falhou.
    const passwordScreen = pathname.startsWith('/redefinir-senha')
        ? <ResetPasswordScreen />
        : environment.features.passwordReset && !authenticated && pathname.startsWith('/esqueci-senha')
            ? <ForgotPasswordScreen />
            : null;

    // Sem sessão o sistema não é montado: nem menu, nem rotas, nem chamadas.
    if (!authenticated || passwordScreen)
        return (
            <ThemeProvider theme={darkMode ? themeDark : theme}>
                <CssBaseline />
                {passwordScreen ?? <LoginScreen />}
            </ThemeProvider>
        );

    return direction === 'rtl' ? (
        <CacheProvider value={rtlCache}>
            <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
                <CssBaseline />
                <GlobalStyles styles={scrollbarStyles(darkMode)} />
                {layout === 'dashboard' && (
                    <>
                        <Sidenav
                            color={sidenavColor}
                            brand={
                                (transparentSidenav && !darkMode) || whiteSidenav
                                    ? logoSidenavBranca
                                    : logoSidenavBranca
                            }
                            brandName="mgFlow"
                            routes={routesApp}
                        />
                        <Configurator />
                        {configsButton}
                    </>
                )}
                <RoutesApp />
            </ThemeProvider>
            <LoadingScreen loading={isLoading} />
        </CacheProvider>
    ) : (
        <ThemeProvider theme={darkMode ? themeDark : theme}>
            <CssBaseline />
            <GlobalStyles styles={scrollbarStyles(darkMode)} />
            {layout === 'dashboard' && (
                <>
                    <Sidenav
                        color={sidenavColor}
                        brand={
                            (transparentSidenav && !darkMode) || whiteSidenav
                                ? logoSidenavBranca
                                : logoSidenavPreta
                        }
                        brandName="mgFlow"
                        routes={routesApp}
                    />
                    <Configurator />
                </>
            )}
            <RoutesApp />
            <LoadingScreen loading={isLoading} />
        </ThemeProvider>
    );
}
